/**
 * GTCC (GENIUSTRACKER CONTROL COCKPIT) Mono-Deck view registry.
 *
 * G1 shipped a shell skeleton only: 6 nav groups / initially 17 stub views, structure
 * taken from the Teil-A report. Source: gt-shared/gtcc-mockup-v6.html,
 * sha a338b614.
 *
 * `label` fields hold i18n catalog KEYS (under `admin.gtcc.group.*` /
 * `admin.gtcc.view.*`), not raw display text — this module has no React
 * context to call `useLocale()` from, so every consumer resolves the
 * display text itself via `t(group.label)` / `t(view.label)`. See
 * `packages/i18n/src/catalogs/en.json` for the English values.
 *
 * E-GTCC-29 adds the view SLOT: a view may name its own `component`. The
 * shell renders that component; a view without one keeps falling back to
 * `GtccStubView`. This is a content slot, not a router — the page switch
 * stays React state in `GtccShell`, exactly as gtcc1 switches pages in
 * `apps/admin/src/AdminApp.tsx:299`.
 */
import type { ComponentType } from "react";

/*
 * C-GTCC-VIEW-OVW1: `ovw` and `risk` now render the 1:1 views of the GTCC
 * reference (`OvwView`, `RiskView`). The predecessors `GtccOverviewView` and
 * `views/risk/GtccRiskView` stay on disk on purpose — the instruction forbids
 * deleting them before D-GTCC-SHELL1 is merged.
 */
/*
 * A-GTCC-VIEW-PSET-DZ1: `pset` und `dz` rendern die 1:1-Views der Referenz
 * (`PsetView`, `DzView`). Die Vorgaenger `GtccSettingsView` und
 * `views/dz/GtccDangerZoneView` bleiben mit Absicht auf der Platte — die
 * Instruktion verbietet ihr Loeschen. `DzView` blendet denselben
 * `PurgeCenterContent` ein, den `GtccDangerZoneView` gemountet hat.
 */
import { PsetView } from "./views/pset/PsetView.js";
import { DzView } from "./views/dz/DzView.js";
import { OvwView } from "./views/ovw/OvwView.js";
import { GtccCockpitView } from "./views/cockpit/GtccCockpitView.js";
import { TenView } from "./views/ten/TenView.js";
import { GtccBrandingView } from "./views/branding/GtccBrandingView.js";
import { GtccDeviceApprovalView } from "./views/approval/GtccDeviceApprovalView.js";
import { HwView } from "./views/hw/HwView.js";
import { DiagView } from "./views/diag/DiagView.js";
import { AiaView } from "./views/aia/AiaView.js";
import { DplaneView } from "./views/dplane/DplaneView.js";
import { GtccTelegramHealthView } from "./views/tg/GtccTelegramHealthView.js";
import { GwView } from "./views/gw/GwView.js";
import { GtccPermissionsView } from "./views/perm/GtccPermissionsView.js";
import { GtccCatalogView } from "./views/cat/GtccCatalogView.js";
import { GtccPlatformGrantsView } from "./views/pgrant/GtccPlatformGrantsView.js";
import { GtccDomainRoutingView } from "./views/dom/GtccDomainRoutingView.js";
import { GtccSupportModeView } from "./views/sup/GtccSupportModeView.js";
import { GtccEmailDeliveriesView as Edel } from "./views/edel/GtccEmailDeliveriesView.js";
import { RiskView } from "./views/risk/RiskView.js";
import { NotiView } from "./views/noti/NotiView.js";
import { AuditView } from "./views/audit/AuditView.js";
import { GtccSupportInboxView } from "./views/supin/GtccSupportInboxView.js";
import { MailView } from "./views/mail/MailView.js";
import { SecView } from "./views/sec/SecView.js";
import { GtccPeopleView } from "./views/ppl/GtccPeopleView.js";
import { GtccPurgeView } from "./views/purge/GtccPurgeView.js";
import { GtccProfileView } from "./views/prof/GtccProfileView.js";
import { GtccTenantModulesView } from "./views/tmod/GtccTenantModulesView.js";
/*
 * A-GTCC-VIEW-ALIN1: `alin` and `launch` now render the 1:1 reference views.
 * The predecessor modules (`views/alerts/GtccAlertsView.tsx`,
 * `views/launch/GtccCustomerLaunchView.tsx`) stay in the tree until
 * D-GTCC-SHELL1 is merged — the instruction forbids deleting them before that
 * — they are only no longer wired into the registry.
 */
import { AlinView } from "./views/alin/AlinView.js";
import { LaunchView } from "./views/launch/LaunchView.js";
export type GtccGroupId =
  | "control"
  | "observability"
  | "administration"
  | "communication"
  | "governance"
  | "security";

/** Single source of truth for the label KEY so a future rename stays cheap. */
export const GTCC_CONTROL_GROUP_LABEL = "admin.gtcc.group.control";

export type GtccPillTone = "crit" | "warn";

/** Closed set of deterministic summaries available in the global header panel. */
export type GtccAskGeniusSummaryKey =
  | "launch"
  | "risk"
  | "alin"
  | "cockpit"
  | "diag"
  | "dplane"
  | "aia"
  | "ten"
  | "hw"
  | "gw"
  | "dom"
  | "mail"
  | "notifications"
  | "audit"
  | "pset"
  | "security"
  | "dz"
  | "smoke";

export interface GtccPill {
  readonly text: string;
  readonly tone: GtccPillTone;
}

export interface GtccStubHint {
  /** i18n key — ein Satz, was in dieser View fehlt. */
  readonly missing: string;
  /** i18n key — das gtcc1-Pendant als Text, ohne Link. */
  readonly pendant: string;
}

export interface GtccViewDef {
  readonly key: string;
  readonly groupId: GtccGroupId;
  /** i18n catalog key — resolve with `t(view.label)`. */
  readonly label: string;
  /** Read-only, evidence-backed summary rendered by the global Ask Genius panel. */
  readonly askGeniusSummary: GtccAskGeniusSummaryKey;
  readonly pill?: GtccPill;
  readonly danger?: boolean;
  /**
   * D-GTCC-FRAME1 — sidebar-only visual marker (recolours label + icon),
   * distinct from `danger` (which gates the in-view danger banner).
   * `GTCC_REFERENCE_2026-09-01.html` applies its `.danger` class to exactly
   * one sidebar row, `dz` — never to `ten`, `audit` or `pset` — so only `dz`
   * carries this flag. D-GTCC-ACCEPT-SHELL1: the reference shows no in-view
   * danger banner on any page, so `pset` and `dz` no longer set `danger`.
   */
  readonly dangerNavMark?: boolean;
  /**
   * A-GTCC-VIEW-BOUNDARY2 — die react-query-Schluessel, die dieser View
   * gehoeren, als Praefixe. Der View-Boundary-Retry raeumt genau diese aus
   * dem Cache, damit ein kaputter Payload nicht den naechsten Versuch
   * ueberlebt. Gemessen, nicht abgeleitet: der zweite Abschnitt ist nicht
   * immer der View-Schluessel, und manche Views haengen an `admin`-Keys.
   * `[]` heisst: diese View fetcht nichts. `["gtcc"]` allein ist verboten,
   * das wuerde die globale Ask-Genius-Leiste mitreissen.
   */
  readonly queryPrefixes: readonly (readonly unknown[])[];
  /**
   * The view's real page. Views without one render `GtccStubView`; the
   * shell owns that fallback, so this stays optional while the cockpit is
   * built out view by view.
   */
  readonly component?: ComponentType;
  /**
   * A-GTCC-ST4 — der gemeinsame Hinweis der noch nicht gebauten Views.
   * Beide Felder sind i18n-Keys: `missing` ist ein Satz, was hier fehlt,
   * `pendant` nennt die Entsprechung in gtcc1 als reinen Text. Ohne
   * `stub` bleibt der allgemeine Platzhaltersatz stehen.
   */
  readonly stub?: GtccStubHint;
  /**
   * D-GTCC-SHELL1 — GTCC_1ZU1_PROGRAM.md Abschnitt "Struktur". Nur die 17
   * Referenz-Eintraege bleiben ohne `parentKey` und erscheinen im Menue
   * (`GTCC_GROUPS`); jede uebrige View traegt hier den Schluessel ihres
   * Eltern-Views. Der Eltern-View rendert seine Kinder als Tab-Leiste
   * (Referenz P18/P19); Deep-Links auf den Kind-Schluessel leiten auf
   * Eltern-View plus Tab um (`deep-link.ts`).
   */
  readonly parentKey?: string;
  /**
   * i18n-Key fuer den Tab-Titel unter dem Eltern-View. Fehlt er, faellt die
   * Tab-Leiste auf `label` zurueck (siehe `GtccShell.tsx`).
   */
  readonly tabLabel?: string;
}

export interface GtccGroupDef {
  readonly id: GtccGroupId;
  /** i18n catalog key — resolve with `t(group.label)`. */
  readonly label: string;
  readonly viewKeys: readonly string[];
}

/**
 * D-GTCC-SHELL1 — GTCC_1ZU1_PROGRAM.md Abschnitt "Struktur": genau diese 17
 * Eintraege, in dieser Reihenfolge, keine weiteren im Menue. Alle bisher
 * zusaetzlich gelisteten Views (dbops, aiwb, gicov, aihealth, smoke, brand,
 * approval, purge, supin, sup, pgrant, prof, perm, dom, tg, edel, ppl, cat, tmod)
 * sind jetzt Unter-Tabs eines dieser 17 Eltern-Views (`GtccViewDef.parentKey`).
 */
export const GTCC_GROUPS: readonly GtccGroupDef[] = [
  { id: "control", label: GTCC_CONTROL_GROUP_LABEL, viewKeys: ["ovw", "alin", "risk", "launch"] },
  {
    id: "observability",
    label: "admin.gtcc.group.observability",
    viewKeys: ["cockpit", "diag", "dplane", "aia"],
  },
  {
    id: "administration",
    label: "admin.gtcc.group.administration",
    viewKeys: ["ten", "hw", "gw"],
  },
  {
    id: "communication",
    label: "admin.gtcc.group.communication",
    viewKeys: ["mail", "noti"],
  },
  {
    id: "governance",
    label: "admin.gtcc.group.governance",
    viewKeys: ["audit", "pset"],
  },
  {
    id: "security",
    label: "admin.gtcc.group.security",
    viewKeys: ["sec", "dz"],
  },
];

export const GTCC_VIEWS: readonly GtccViewDef[] = [
  {
    key: "ovw",
    // Fetcht unter overview, nicht unter dem View-Schluessel.
    queryPrefixes: [["gtcc", "overview"]],
    groupId: "control",
    label: "admin.gtcc.view.ovw",
    askGeniusSummary: "launch",
    component: OvwView,
  },
  {
    key: "alin",
    queryPrefixes: [["gtcc", "alerts"]],
    groupId: "control",
    label: "admin.gtcc.view.alin",
    askGeniusSummary: "alin",
    component: AlinView,
  },
  {
    key: "risk",
    queryPrefixes: [["gtcc", "risk"]],
    groupId: "control",
    label: "admin.gtcc.view.risk",
    askGeniusSummary: "risk",
    component: RiskView,
  },
  {
    key: "launch",
    queryPrefixes: [["gtcc", "launch"]],
    groupId: "control",
    label: "admin.gtcc.view.launch",
    askGeniusSummary: "launch",
    component: LaunchView,
  },
  {
    key: "cockpit",
    queryPrefixes: [["gtcc", "cockpit"]],
    groupId: "observability",
    label: "admin.gtcc.view.cockpit",
    askGeniusSummary: "cockpit",
    component: GtccCockpitView,
  },
  {
    // P19: Smoke Dashboard, Recent Errors, Permission Diagnostics und der
    // Service-/Config-Snapshot liegen in "Diagnostics". Der eingebettete
    // Smoke-Block fetcht weiter unter `["gtcc","smoke"]` — deshalb zwei
    // Praefixe.
    key: "diag",
    queryPrefixes: [
      ["gtcc", "diag"],
      ["gtcc", "smoke"],
    ],
    groupId: "observability",
    label: "admin.gtcc.view.diag",
    askGeniusSummary: "diag",
    component: DiagView,
  },
  {
    key: "dplane",
    queryPrefixes: [["gtcc", "dplane"]],
    groupId: "observability",
    label: "admin.gtcc.view.dplane",
    askGeniusSummary: "dplane",
    component: DplaneView,
  },
  {
    // P19: DB Health Center, DB Ops Control Plane + Plan Builders und DB
    // Monitoring & Alerts sind in "Data Plane" zusammengelegt. `dbops` bleibt
    // als Schluessel bestehen und zeigt auf denselben View; es fetcht damit
    // unter `["gtcc","dplane"]`, nicht mehr unter `["gtcc","dbops"]`.
    key: "dbops",
    queryPrefixes: [["gtcc", "dplane"]],
    groupId: "observability",
    label: "admin.gtcc.view.dbops",
    askGeniusSummary: "dplane",
    component: DplaneView,
    parentKey: "dplane",
  },
  {
    // P19: Provider-Workbench, GI Coverage, Question Analytics und AI Copilot
    // liegen in "AI & Analytics". Fetcht unter insights-analytics, nicht unter
    // dem View-Schluessel.
    key: "aia",
    queryPrefixes: [["gtcc", "insights-analytics"]],
    groupId: "observability",
    label: "admin.gtcc.view.aia",
    askGeniusSummary: "aia",
    component: AiaView,
  },
  {
    // P19: zusammengelegt in "AI & Analytics"; der Schluessel bleibt und fetcht
    // damit unter `["gtcc","insights-analytics"]`, nicht mehr unter `aiwb`.
    key: "aiwb",
    queryPrefixes: [["gtcc", "insights-analytics"]],
    groupId: "observability",
    label: "admin.gtcc.view.aiwb",
    askGeniusSummary: "aia",
    component: AiaView,
    parentKey: "aia",
  },
  {
    // P19: zusammengelegt in "AI & Analytics".
    key: "gicov",
    queryPrefixes: [["gtcc", "insights-analytics"]],
    groupId: "observability",
    label: "admin.gtcc.view.gicov",
    askGeniusSummary: "aia",
    component: AiaView,
    parentKey: "aia",
  },
  {
    // P19: zusammengelegt in "AI & Analytics".
    key: "aihealth",
    queryPrefixes: [["gtcc", "insights-analytics"]],
    groupId: "observability",
    label: "admin.gtcc.view.aihealth",
    askGeniusSummary: "risk",
    component: AiaView,
    parentKey: "aia",
  },
  {
    // P19: zusammengelegt in "Diagnostics"; der Schluessel bleibt und zeigt auf
    // denselben View, der den Smoke-Block einbettet.
    key: "smoke",
    queryPrefixes: [
      ["gtcc", "diag"],
      ["gtcc", "smoke"],
    ],
    groupId: "observability",
    label: "admin.gtcc.view.smoke",
    askGeniusSummary: "smoke",
    component: DiagView,
    parentKey: "diag",
  },
  {
    // A-GTCC-DZ1: Tenant 360 fuehrt in gtcc1 zum harten Geraete-Loeschen
    // (`apps/admin/src/tenants/TenantDevicesCard.tsx:256`, `?confirm=hard`)
    // und zum Entfernen von Mitgliedern; `GTCC_REFERENCE_2026-09-01.html`
    // rahmt aber keine der fuenf `ten`-Tabs als Gefahrenzone — der rote
    // Rahmen/Banner gehoert nur den einzelnen destruktiven Aktionen darin
    // (D-GTCC-FRAME1, A5).
    key: "ten",
    // E-GTCC-VIEW-TEN1: die Referenz-View "Tenants & Access" mit fuenf Tabs.
    // Die alte View `GtccTenantsView` bleibt als Bausteinquelle bestehen — ihre
    // Karten sind in den Tabs wiederverwendet, geloescht wird sie erst mit
    // D-GTCC-SHELL1.
    // Die Tenant-Karten lesen und invalidieren zusaetzlich admin-Schluessel.
    queryPrefixes: [
      ["gtcc", "ten"],
      ["admin", "tenants"],
    ],
    groupId: "administration",
    label: "admin.gtcc.view.ten",
    askGeniusSummary: "ten",
    component: TenView,
  },
  {
    key: "brand",
    // Fetcht unter branding, nicht unter dem View-Schluessel.
    queryPrefixes: [["gtcc", "branding"]],
    groupId: "administration",
    label: "admin.gtcc.view.brand",
    askGeniusSummary: "pset",
    component: GtccBrandingView,
    parentKey: "ten",
  },
  {
    // G-GTCC-28-LIFT1: this projection exposes only the three GET-backed
    // approval lists. Approve and reject controls remain absent by design.
    key: "approval",
    // Fetcht unter device-approval, nicht unter dem View-Schluessel.
    queryPrefixes: [["gtcc", "device-approval"]],
    groupId: "administration",
    label: "admin.gtcc.device_approval.title",
    askGeniusSummary: "ten",
    component: GtccDeviceApprovalView,
    parentKey: "ten",
  },
  {
    // B-GTCC-PURGE1: read-only Historie ueber `GET /v1/admin/tenants/purge-jobs`.
    // Das gtcc1-Pendant (`apps/admin/src/purge/PurgeCenterPage.tsx:74`) loescht
    // Tenant-Daten unwiderruflich — deshalb Gefahrenzone, wie bei `audit`.
    key: "purge",
    queryPrefixes: [["gtcc", "purge"]],
    groupId: "administration",
    label: "admin.gtcc.purge.label",
    askGeniusSummary: "ten",
    component: GtccPurgeView,
    danger: true,
    parentKey: "ten",
  },
  {
    /*
     * E-GTCC-MODUL-ENTZUG1 (003586): Module je Mandant. Der Owner gibt beim
     * Sammeldeploy alle Module manuell frei und schaltet die nicht gebuchten
     * danach hier selbst ab; die Spalte "gebucht laut Plan" sagt ihm, welche
     * Freigabe er anfassen darf. Liest `GET /v1/admin/tenants` und
     * `GET /v1/admin/tenants/:tenantId/module-grants`, schreibt ueber
     * `POST /v1/admin/tenants/:tenantId/module-grants/:grantId/end`.
     * Beide Reads liegen unter `["gtcc","tmod",...]`, ein Praefix genuegt.
     * `danger`, weil ein Entzug einem Mandanten im laufenden Betrieb ein
     * Modul nimmt (90 Tage Nur-Lesen, dann zu) — wie bei `purge`.
     */
    key: "tmod",
    queryPrefixes: [["gtcc", "tmod"]],
    groupId: "administration",
    label: "admin.gtcc.view.tmod",
    askGeniusSummary: "ten",
    component: GtccTenantModulesView,
    danger: true,
    parentKey: "ten",
  },
  {
    /*
     * C-GTCC-VIEW-HW-GW2: `hw` rendert die 1:1-View der Referenz (`HwView`,
     * Zeilen 736-770). Die Vorgaengerin `views/hw/GtccHardwareView.tsx` bleibt
     * wie bei `ovw`/`risk` auf Platte, nur nicht mehr verdrahtet. Alle fuenf
     * Reads der View liegen unter `["gtcc","hw",…]` (siehe `api/hw.ts`),
     * darunter auch die, deren Pfad `devices/pending`, `ops/vehicle-flow` und
     * `managed-hardware/pool` heisst — ein Praefix genuegt deshalb.
     */
    key: "hw",
    queryPrefixes: [["gtcc", "hw"]],
    groupId: "administration",
    label: "admin.gtcc.view.hw",
    askGeniusSummary: "hw",
    component: HwView,
  },
  {
    /*
     * C-GTCC-VIEW-HW-GW2: `gw` rendert die 1:1-View der Referenz (`GwView`,
     * Zeilen 772-805). Die Vorgaengerin `views/gw/GtccGatewayView.tsx` bleibt
     * auf Platte. Alle vier Reads liegen unter `["gtcc","gw",…]`
     * (siehe `api/gw.ts`).
     */
    key: "gw",
    queryPrefixes: [["gtcc", "gw"]],
    groupId: "administration",
    label: "admin.gtcc.view.gw",
    askGeniusSummary: "gw",
    component: GwView,
  },
  {
    key: "dom",
    queryPrefixes: [["gtcc", "dom"]],
    groupId: "governance",
    label: "admin.gtcc.view.dom",
    askGeniusSummary: "dom",
    component: GtccDomainRoutingView,
    parentKey: "pset",
  },
  {
    key: "mail",
    // G-GTCC-MAIL-WIRE1: die 1:1-Referenzansicht liest die GTCC-Summary.
    queryPrefixes: [["gtcc", "mail", "summary"]],
    groupId: "communication",
    label: "admin.gtcc.view.mail",
    askGeniusSummary: "mail",
    component: MailView,
  },
  {
    key: "tg",
    // TelegramHealthContent bekommt queryScope="gtcc" und fetcht dann hier.
    queryPrefixes: [["gtcc", "tg"]],
    groupId: "communication",
    label: "admin.gtcc.view.tg",
    askGeniusSummary: "mail",
    component: GtccTelegramHealthView,
    parentKey: "mail",
  },
  {
    key: "edel",
    // Fetcht unter email-deliveries, nicht unter dem View-Schluessel.
    queryPrefixes: [["gtcc", "email-deliveries"]],
    groupId: "communication",
    label: "admin.gtcc.view.edel",
    askGeniusSummary: "mail",
    component: Edel,
    parentKey: "mail",
  },
  {
    key: "noti",
    queryPrefixes: [["gtcc", "noti"]],
    groupId: "communication",
    label: "admin.gtcc.view.noti",
    askGeniusSummary: "notifications",
    component: NotiView,
  },
  {
    key: "supin",
    // Fetcht unter support-tickets, nicht unter dem View-Schluessel.
    queryPrefixes: [["gtcc", "support-tickets"]],
    groupId: "administration",
    label: "admin.gtcc.view.supin",
    askGeniusSummary: "notifications",
    component: GtccSupportInboxView,
    parentKey: "ten",
  },
  {
    // A-GTCC-DZ1: das gtcc1-Pendant purgt Audit-Zeilen
    // (`apps/admin/src/audit/AuditPage.tsx:177`) — Nachweise gehen
    // unwiderruflich verloren. Gefahrenzone.
    key: "audit",
    queryPrefixes: [["gtcc", "audit"]],
    groupId: "governance",
    label: "admin.gtcc.view.audit",
    askGeniusSummary: "audit",
    component: AuditView,
  },
  {
    key: "cat",
    queryPrefixes: [["gtcc", "cat"]],
    groupId: "governance",
    label: "admin.gtcc.view.cat",
    askGeniusSummary: "pset",
    component: GtccCatalogView,
    parentKey: "pset",
  },
  {
    // A-GTCC-DZ1: die Plattformeinstellungen fuehren ueber `people` zum
    // Widerrufen von Einladungen
    // (`apps/admin/src/invitations/InvitationsPage.tsx:738`). D-GTCC-ACCEPT-SHELL1:
    // die Referenz zeigt hier keinen In-View-Banner, deshalb kein `danger`.
    key: "pset",
    // A-GTCC-VIEW-PSET-DZ1: `PsetView` liest alle drei Quellen unter dem
    // eigenen Schluessel — die Flag-Antwort liegt hier im GtccResult-Umschlag
    // und nicht in der Rohform, die `useGtccSettings` unter `gtcc/settings`
    // ablegt (siehe api/pset.ts).
    queryPrefixes: [["gtcc", "pset"]],
    groupId: "governance",
    label: "admin.gtcc.view.pset",
    askGeniusSummary: "pset",
    component: PsetView,
  },
  {
    key: "perm",
    // Fetcht nichts: weder die View noch ihre Importe rufen useQuery.
    queryPrefixes: [],
    groupId: "administration",
    label: "admin.gtcc.view.perm",
    askGeniusSummary: "diag",
    component: GtccPermissionsView,
    parentKey: "ten",
  },
  {
    // docs/design/GTCC_REFERENCE_SPEC.md (A-GTCC-REF1, jetzt gemergt) §7
    // bestaetigt: "ppl, cat -> aia / pset per the registry" (ppl -> aia,
    // cat -> pset).
    key: "ppl",
    // useGtccPeople invalidiert zusaetzlich admin-Schluessel der Mitglieder.
    queryPrefixes: [
      ["gtcc", "ppl"],
      ["admin", "invitations"],
      ["admin", "members"],
    ],
    groupId: "observability",
    label: "admin.gtcc.view.ppl",
    askGeniusSummary: "pset",
    component: GtccPeopleView,
    parentKey: "aia",
  },
  {
    key: "sec",
    queryPrefixes: [["gtcc", "sec"]],
    groupId: "security",
    label: "admin.gtcc.view.sec",
    askGeniusSummary: "security",
    component: SecView,
  },
  {
    // A-GTCC-18: eigene Identitaet/Rollen/Sitzung/Sicherheitsereignisse,
    // read-only. Grants (`platform-grants.routes.ts`) und MFA
    // (`mfa.routes.ts`) haben keinen Read-Endpunkt, siehe View-Doku.
    key: "prof",
    queryPrefixes: [["gtcc", "prof"]],
    groupId: "administration",
    label: "admin.gtcc.view.prof",
    askGeniusSummary: "security",
    component: GtccProfileView,
    parentKey: "ten",
  },
  {
    key: "pgrant",
    queryPrefixes: [["gtcc", "pgrant"]],
    groupId: "administration",
    label: "admin.gtcc.view.pgrant",
    askGeniusSummary: "audit",
    component: GtccPlatformGrantsView,
    parentKey: "ten",
  },
  {
    key: "sup",
    // Rendert GtccPlatformGrantsView; eigene Queries hat die View nicht.
    queryPrefixes: [["gtcc", "pgrant"]],
    groupId: "administration",
    label: "admin.gtcc.view.sup",
    askGeniusSummary: "audit",
    component: GtccSupportModeView,
    parentKey: "ten",
  },
  {
    key: "dz",
    // A-GTCC-VIEW-PSET-DZ1: `DzView` liest die Job-Historie unter dem eigenen
    // Schluessel `gtcc/dz` (GtccResult-Umschlag, nicht die Rohform von
    // `GtccPurgeView`, siehe api/dz.ts) und blendet den unveraenderten
    // PurgeCenterContent ein — dessen Daten liegen weiterhin komplett unter
    // admin.
    queryPrefixes: [
      ["gtcc", "dz"],
      ["admin", "purge-jobs"],
      ["admin", "purge-tenant-picker"],
      ["admin", "tenants"],
    ],
    groupId: "security",
    label: "admin.gtcc.view.dz",
    askGeniusSummary: "dz",
    dangerNavMark: true,
    component: DzView,
  },
];

export const GTCC_DEFAULT_VIEW_KEY = "cockpit";

/**
 * D-GTCC-SHELL1 — die 17 kanonischen Sidebar-Eintraege (ein Eintrag pro
 * `GTCC_GROUPS[].viewKeys`), in Sidebar-Reihenfolge. Views mit `parentKey`
 * sind Tab-Kinder und erscheinen hier nicht.
 */
export const GTCC_TOP_LEVEL_VIEWS: readonly GtccViewDef[] = GTCC_GROUPS.flatMap((group) =>
  group.viewKeys
    .map((key) => GTCC_VIEWS.find((v) => v.key === key))
    .filter((v): v is GtccViewDef => v !== undefined),
);

export function gtccViewByKey(key: string): GtccViewDef | undefined {
  return GTCC_VIEWS.find((v) => v.key === key);
}

export function gtccGroupById(id: GtccGroupId): GtccGroupDef | undefined {
  return GTCC_GROUPS.find((g) => g.id === id);
}

/**
 * D-GTCC-SHELL1 — die Tabs eines Eltern-Views, in Registrierungsreihenfolge.
 * `[]` heisst: der View hat keine Kinder und rendert ohne Tab-Leiste.
 */
export function gtccChildrenOf(parentKey: string): readonly GtccViewDef[] {
  return GTCC_VIEWS.filter((v) => v.parentKey === parentKey);
}
